/**
 * InterventionSimulatorPage.jsx — iter903 (Feb 2026)
 *
 * Patient-friendly page: choose condition → see ranked list of foods /
 * compounds / mind-body practices that modulate the underlying KEGG
 * pathways. Foods resolved against Nutramilo/USDA validated DB with
 * calories, macros, NIS and DIL.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Loader2, Sparkles, Apple, Pill, Brain,
  ChevronDown, ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import EducationalRibbon from "@/components/EducationalRibbon";

const EV_COLORS = {
  high:     "from-emerald-500 to-teal-500",
  moderate: "from-amber-500 to-orange-500",
  low:      "from-slate-500 to-slate-600",
};
const EV_LABEL = { high: "Силна доказ.", moderate: "Умерена", low: "Ограничена" };

const CATEGORY_LABEL = {
  hormonal:  "Хормонални", metabolic: "Метаболитни", genetic: "Генетични",
  nutrition: "Хранителни", biomarker: "Биомаркер", cardio: "Сърдечно-съдови",
  autoimmune:"Автоимунни", other: "Други",
};

export default function InterventionSimulatorPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();

  const [conditions, setConditions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/phenotype-explorer/conditions`)
      .then(res => {
        setConditions(res.data.conditions || []);
        const def = (res.data.conditions || []).find(c => c.id === "pcos_a")
                 || res.data.conditions?.[0];
        if (def) setSelected(def.id);
      }).catch(() => setConditions([]));
  }, [API]);

  const load = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/intervention-simulator/condition/${selected}`);
      setData(data);
    } catch (e) { setData(null); }
    finally { setLoading(false); }
  }, [API, selected]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = {};
    conditions.forEach(c => {
      const cat = CATEGORY_LABEL[c.category] || c.category;
      if (!g[cat]) g[cat] = [];
      g[cat].push(c);
    });
    return g;
  }, [conditions]);

  const bg = isDark ? "bg-[#0a0a12] text-gray-100" : "bg-slate-50 text-gray-900";
  const cardCls = isDark ? "border-slate-700 bg-slate-900/50" : "border-slate-200 bg-white";

  return (
    <div className={`min-h-screen ${bg}`} data-testid="intervention-simulator-page">
      <SEO title="Intervention Simulator — Nutramilo" description="Виж кои храни, добавки и mind-body практики модулират KEGG пътищата на твоя фенотип." />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-xs opacity-70 hover:opacity-100 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Назад
        </button>

        <EducationalRibbon lang="bg" isDark={isDark} variant="banner" className="mb-4" />

        <div className="flex items-start gap-3 mb-6">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 grid place-items-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold">Intervention Impact Simulator</h1>
            <p className={`text-xs sm:text-sm mt-1 ${isDark ? "text-slate-400" : "text-slate-600"}`}>
              Избери фенотип → виж проверени храни (Nutramilo/USDA), съединения и mind-body практики,
              които модулират неговите KEGG пътища.
            </p>
          </div>
        </div>

        {/* Condition dropdown */}
        <div className={`rounded-2xl border p-4 mb-4 ${cardCls}`}>
          <label className="text-[10px] uppercase tracking-wider font-bold opacity-60">Избери фенотип</label>
          <div className="relative mt-1 max-w-md">
            <select
              value={selected || ""}
              onChange={(e) => setSelected(e.target.value)}
              data-testid="condition-select"
              className={`w-full appearance-none rounded-lg px-3 py-2 text-sm border ${
                isDark ? "bg-slate-800 border-slate-700 text-slate-100" : "bg-white border-slate-300"
              }`}
            >
              <option value="">— избери —</option>
              {Object.entries(grouped).map(([cat, items]) => (
                <optgroup key={cat} label={cat}>
                  {items.map(c => <option key={c.id} value={c.id}>{c.name_bg}</option>)}
                </optgroup>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40 pointer-events-none" />
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin opacity-40" />
          </div>
        )}

        {!loading && data && (
          <div className="space-y-4">
            <div className={`rounded-2xl border p-4 ${cardCls}`} data-testid="condition-summary">
              <h2 className="text-lg font-bold">{data.name_bg}</h2>
              <div className="text-xs opacity-70 mt-1">
                {data.pathway_coverage}/{data.pathway_count} KEGG пътища с активен интервенционен план ·
                {" "}{data.foods.length} храни · {data.compounds.length} съединения · {data.mind_body.length} mind-body
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Foods */}
              <section className={`rounded-2xl border p-4 ${cardCls}`} data-testid="col-foods">
                <div className="flex items-center gap-2 mb-3">
                  <Apple className="w-4 h-4 text-emerald-500" />
                  <h3 className="font-bold text-sm">Храни (Nutramilo / USDA)</h3>
                </div>
                <div className="space-y-2">
                  {data.foods.length === 0 && <div className="text-xs opacity-60">Няма данни.</div>}
                  {data.foods.map((f, i) => (
                    <FoodCard key={i} food={f} isDark={isDark} testid={`food-${i}`} />
                  ))}
                </div>
              </section>

              {/* Compounds */}
              <section className={`rounded-2xl border p-4 ${cardCls}`} data-testid="col-compounds">
                <div className="flex items-center gap-2 mb-3">
                  <Pill className="w-4 h-4 text-violet-500" />
                  <h3 className="font-bold text-sm">Добавки & съединения</h3>
                </div>
                <div className="space-y-2">
                  {data.compounds.map((c, i) => (
                    <div key={i} className={`rounded-lg border p-2.5 ${
                      isDark ? "border-slate-700 bg-slate-800/40" : "border-slate-200 bg-slate-50"
                    }`} data-testid={`compound-${i}`}>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div>
                          <div className="text-xs font-bold">{c.name_bg}</div>
                          <code className="text-[10px] opacity-60">{c.name_en}</code>
                        </div>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full text-white bg-gradient-to-r ${EV_COLORS[c.evidence] || EV_COLORS.low}`}>
                          {EV_LABEL[c.evidence] || c.evidence}
                        </span>
                      </div>
                      <div className="text-[11px] opacity-80 mt-1">
                        <b>Механизъм:</b> {c.mechanism}
                      </div>
                      <div className="text-[11px] opacity-80 mt-0.5">
                        <b>Ефект:</b> {c.effect}
                      </div>
                      <div className={`text-[11px] font-semibold mt-1 ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                        Доза: {c.dose_bg}
                      </div>
                      {c.pathways && c.pathways.length > 1 && (
                        <div className="text-[9px] opacity-50 mt-1">Модулира {c.pathways.length} пътя</div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Mind-body */}
              <section className={`rounded-2xl border p-4 ${cardCls}`} data-testid="col-mind-body">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-4 h-4 text-pink-500" />
                  <h3 className="font-bold text-sm">Mind-body практики</h3>
                </div>
                <div className="space-y-2">
                  {data.mind_body.map((m, i) => (
                    <div key={i} className={`rounded-lg border p-2.5 ${
                      isDark ? "border-slate-700 bg-slate-800/40" : "border-slate-200 bg-slate-50"
                    }`} data-testid={`mindbody-${i}`}>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="text-xs font-bold">{m.name_bg}</div>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full text-white bg-gradient-to-r ${EV_COLORS[m.evidence] || EV_COLORS.low}`}>
                          {EV_LABEL[m.evidence] || m.evidence}
                        </span>
                      </div>
                      <div className="text-[11px] opacity-80"><b>Механизъм:</b> {m.mechanism}</div>
                      <div className="text-[11px] opacity-80 mt-0.5"><b>Протокол:</b> {m.protocol_bg}</div>
                      <div className={`text-[11px] font-semibold mt-1 ${isDark ? "text-pink-300" : "text-pink-700"}`}>
                        Дозировка: {m.weekly_dose_bg}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Trust footer */}
            <div className={`rounded-2xl border p-3 text-xs ${cardCls} flex items-start gap-2`}>
              <ShieldCheck className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <div className="opacity-80">
                Храните са резолвирани срещу <b>Nutramilo</b> (валидирана) и <b>USDA</b> бази.
                Съединенията са куратирани от литература (evidence tier: high · moderate · low).
                Mind-body — валидирани протоколи от peer-reviewed изследвания.
                <br/><b>Не е медицински съвет.</b> Консултирайте лекар преди старт.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function FoodCard({ food, isDark, testid }) {
  const r = food.resolved;
  return (
    <div className={`rounded-lg border p-2.5 ${
      isDark ? "border-slate-700 bg-slate-800/40" : "border-slate-200 bg-slate-50"
    }`} data-testid={testid}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="text-xs font-bold">{food.name_bg}</div>
        {r && (
          <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
            r.source === "nutrami"
              ? "bg-emerald-500/20 text-emerald-500"
              : "bg-slate-500/20 text-slate-500"
          }`}>{r.source === "nutrami" ? "Nutramilo ✓" : "USDA"}</span>
        )}
      </div>
      <div className="text-[11px] opacity-80"><b>Активно:</b> {food.compound}</div>
      <div className="text-[11px] opacity-80 mt-0.5"><b>Ефект:</b> {food.effect}</div>
      {r && (
        <div className="grid grid-cols-4 gap-1 mt-1.5 text-[10px]">
          {r.calories != null && <MiniStat label="kcal" value={Math.round(r.calories)} />}
          {r.protein  != null && <MiniStat label="P"   value={`${Math.round(r.protein)}g`} />}
          {r.nis_percent != null && <MiniStat label="NIS" value={`${Math.round(r.nis_percent)}%`} />}
          {r.dil_g != null && <MiniStat label="DIL" value={Math.round(r.dil_g)} />}
        </div>
      )}
      {food.pathways && food.pathways.length > 1 && (
        <div className="text-[9px] opacity-50 mt-1">Модулира {food.pathways.length} пътя</div>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded bg-white/40 dark:bg-white/5 p-1 text-center">
      <div className="text-[8px] uppercase opacity-60">{label}</div>
      <div className="text-[11px] font-bold">{value}</div>
    </div>
  );
}
